<template>
  <div style="min-height: calc(100vh - 158px)">
    <!-- <div v-if="!submenus.length">No item found</div> -->
    <template v-if="isDashboard(selectedSidebar)">
      <div class="dashboard-toolbar" v-if="company && company.company_type === 'group'">
        <div class="toolbar-filter">
          <label for="colFormLabel" class="toolbar-label">Companies</label>
          <AsyncSelect
            placeholder="Companies"
            v-model="companyIds"
            :api-service="fetchCompaniesDropdown"
            label="name"
            :multiple="true"
          />
        </div>

        <button
          class="btn-sync go-button"
          :disabled="isSyncing"
          @click="filterDashboardByCompany"
        >
          <span>Go</span>
        </button>
      </div>
      <template v-else>
        <div v-if="!isSyncing && shouldShowSync" class="dashboard-toolbar justify-content-end">
          <span v-if="lastUpdated" class="last-updated">
            <i class="feather icon-clock"></i>
            Last Updated: {{ lastUpdated }}
          </span>
          <button
            class="btn-sync"
            :disabled="isSyncing"
            @click="syncDashboard"
          >
            <img src="/app-assets/images/icons/dashboard/dashboard_sync.png" alt="Sync Icon" class="sync-icon" />
            <span>Sync</span>
          </button>
        </div>
      </template>

      <div v-if="isSyncing" class="syncing-overlay">
        <div class="syncing-message">
          <i class="feather icon-loader animation-spin"></i>
          <p>Syncing dashboard data...</p>
          <div class="progress-bar-container mt-3 mb-2">
            <div class="progress-bar-indeterminate"></div>
          </div>
          <small class="text-muted">This may take a few moments</small>
        </div>
      </div>

      <DynamicDashboard
        :submenus="submenus"
        :class="{ 'pointer-events-none': isSyncing }"
        @updated="handleLastUpdated"
      />
    </template>
    <div v-else>
      <div v-if="!hasPages" class="card shadow p-5 text-center mt-2 w-100">
        <h4 class="text-muted mb-0">
          <i class="feather icon-info mr-1"></i> No pages found.
        </h4>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { inject } from "vue";
import DynamicDashboard from "@/views/page/company/DynamicDashboard.vue";
import handleSidebar from "@/services/modules/sidebar";
import useDate from "@/services/utils/day";
import { getPageRouterPayload } from "@/services/utils/navigation";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleCompany from "@/services/modules/company";
import handleAccounting from "@/services/modules/accounting";

export default {
  name: "SubmenuPage",

  components: {
    AsyncSelect,
    DynamicDashboard,
  },

  setup() {
    const sidebarMethod = handleSidebar();
    const { formatDateWithTime } = useDate();
    const {fetchCompanyInfo} = handleCompany();
    const {fetchCompaniesDropdown} = handleAccounting()
    const showSuccess = inject("showSuccess");
    return {
      sidebarMethod,
      fetchCompanyInfo,
      formatDateWithTime,
      fetchCompaniesDropdown,
      showSuccess,
    };
  },

  data() {
    return {
      isSyncing: false,
      syncStartTime: null,
      syncDuration: "",
      lastUpdated: null,
      company: null,
      companyIds: [],
    };
  },

  watch: {
    sidebar: {
      immediate: true,
      handler() {
        this.checkRedirect();
      },
    },
    "$route": {
      immediate: true,
      handler() {
        this.checkRedirect();
      },
    },
  },

  methods: {
    handleLastUpdated(calculatedAt) {
      if (calculatedAt) {
        this.lastUpdated = this.formatDateWithTime(calculatedAt);
      }
    },
    isDashboard(selectedSidebar) {
      return selectedSidebar?.is_dashboard;
    },

    async syncDashboard() {
      this.isSyncing = true;
      this.syncStartTime = new Date();

      try {
        await this.sidebarMethod.refreshDashboard(
          `?company_id=${this.$route.params.companyId}&module_id=${this.$route.params.moduleId}`,
        );

        // Calculate duration
        const duration = Math.round((new Date() - this.syncStartTime) / 1000);
        this.syncDuration = `${duration} seconds`;

        // Trigger reload event - this will fetch new data and update cache
        window.dispatchEvent(new CustomEvent("reload-dashboard-summaries"));
        this.showSuccess("Dashboard synced successfully!");
      } catch (error) {
        console.error("Sync failed:", error);
      } finally {
        this.isSyncing = false;
      }
    },

    async filterDashboardByCompany() {
      this.isSyncing = true;
      this.syncStartTime = new Date();

      try {
        // Trigger reload event - this will fetch new data and update cache
        window.dispatchEvent(
          new CustomEvent("reload-dashboard-summaries", {
            detail: {
              company_ids: this.companyIds
            }
          })
        );
        this.showSuccess("Dashboard synced successfully!");
      } catch (error) {
        console.error("Sync failed:", error);
      } finally {
        this.isSyncing = false;
      }
    },

    checkRedirect() {
      if (!this.sidebar || this.sidebar.length < 1) return;
      if (this.$route.params.pageId) return;

      if (!this.isDashboard(this.selectedSidebar)) {
        this.redirectToFirstPage();
      }
    },

    redirectToFirstPage() {
      if (!this.submenus || this.submenus.length < 1) return;

      const firstSubmenu = this.submenus[0];
      if (
        !firstSubmenu ||
        !firstSubmenu.pages ||
        firstSubmenu.pages.length < 1
      )
        return;

      const firstPage = firstSubmenu.pages[0];
      if (!firstPage.route) return;

      const payload = getPageRouterPayload(firstPage, {
        companyId: this.$route.params.companyId,
        moduleId: this.$route.params.moduleId,
        menuId: this.$route.params.menuId,
      });

      this.$router.push(payload);
    },
    getCompany() {
      this.fetchCompanyInfo(this.companyId)
        .then((res) => {
          if(res.status){
            this.company = res.data;
          }
        }).catch((error) => {
        console.log(error);
      });
    },
  },

  computed: {
    ...mapGetters({
      sidebar: "company/getSidebar",
    }),

    selectedSidebar() {
      return this.sidebar.find((i) => i.id == this.$route.params.menuId);
    },

    submenus() {
      return this.selectedSidebar?.submenus
        ? this.selectedSidebar.submenus
        : [];
    },

    hasPages() {
      return this.submenus.some((s) => s.pages && s.pages.length > 0);
    },

    shouldShowSync() {
      return this.submenus.some(submenu =>
        submenu.pages.some(page =>
          page.status === 'active' && page.card_type !== 'input' && page.card_type !== 'list'
        )
      );
    },
  },

  mounted() {
    this.getCompany();
  }
};
</script>

<style scoped>
.dashboard-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.toolbar-filter {
  width: 25%;
}

.toolbar-label {
  font-size: 12px;
  font-weight: 600;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.last-updated {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #8a8a8a;
}

.last-updated i {
  font-size: 14px;
}

.btn-sync {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #16a085, #0d8c74);
  color: #ffffff;
  border: none;
  border-radius: 100px;
  padding: 9px 24px;
  font-weight: 600;
  font-size: 12.5px;
  transition: all 0.25s ease;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  box-shadow: 0 4px 10px rgba(22, 160, 133, 0.25);
}

.btn-sync i {
  font-size: 16px;
}

.sync-icon {
  width: 15px;
  height: 15px;
  filter: brightness(0) invert(1);
}

.btn-sync:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(22, 160, 133, 0.35);
}

.btn-sync:active:not(:disabled) {
  transform: translateY(0);
}

.btn-sync:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.go-button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 75px;
  height: 40px;
}

.syncing-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 20, 30, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.syncing-message {
  background: white;
  padding: 32px 54px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}

.syncing-message i {
  font-size: 38px;
  color: #16a085;
  margin-bottom: 15px;
  display: block;
}

.syncing-message p {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.syncing-message small {
  color: #6c757d;
}

.animation-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.pointer-events-none {
  pointer-events: none;
}

.progress-bar-container {
  width: 100%;
  height: 6px;
  background-color: #f1f1f1;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.progress-bar-indeterminate {
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    #28c76f,
    #20c997,
    transparent
  );
  border-radius: 10px;
  width: 50%;
  position: absolute;
  animation: indeterminate 1.5s infinite ease-in-out;
}

@keyframes indeterminate {
  0% {
    left: -50%;
  }
  100% {
    left: 100%;
  }
}
</style>
